import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Copy, Check, RotateCcw, Plus, Trash2, ExternalLink, Monitor, Sparkles,
  Megaphone, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  announcement: { label: "Oppslag", icon: Megaphone },
  campaign: { label: "Kampanje", icon: Sparkles },
  task: { label: "Rutine", icon: CalendarDays },
};

const FRONTEND_URL = (typeof window !== "undefined" && window.location?.origin) || "";

export default function TvScreenSettings() {
  const [token, setToken] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [form, setForm] = useState({
    title: "", body: "", kind: "announcement", active: true,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tv/settings");
      setToken(data.token || "");
      setItems(data.items || []);
    } catch {
      toast.error("Kunne ikke hente TV-innstillinger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tvUrl = useMemo(() => {
    if (!token || !FRONTEND_URL) return "";
    return `${FRONTEND_URL}/tv-dashboard?token=${encodeURIComponent(token)}`;
  }, [token]);

  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch {
      toast.error("Kunne ikke kopiere");
    }
  };

  const rotate = async () => {
    if (!window.confirm(
      "Rullere TV-token? Alle skjermer som bruker det gamle tokenet vil miste tilgang og må aktiveres på nytt.",
    )) return;
    try {
      const { data } = await api.post("/tv/token/rotate");
      setToken(data.token);
      toast.success("Nytt TV-token generert");
    } catch {
      toast.error("Kunne ikke rullere token");
    }
  };

  const add = async () => {
    if (!form.title.trim()) {
      toast.error("Tittel er påkrevd");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/tv/settings/items", {
        title: form.title.trim(),
        body: form.body.trim(),
        kind: form.kind,
        active: form.active,
      });
      setItems((cur) => [...cur, data]);
      setForm({ title: "", body: "", kind: "announcement", active: true });
      toast.success("Lagt til på kontorskjermen");
    } catch {
      toast.error("Kunne ikke lagre oppslag");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (item) => {
    try {
      const { data } = await api.patch(`/tv/settings/items/${item.id}`, {
        active: !item.active,
      });
      setItems((cur) => cur.map((x) => (x.id === item.id ? data : x)));
    } catch {
      toast.error("Kunne ikke oppdatere");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Slette «${item.title}» fra kontorskjermen?`)) return;
    try {
      await api.delete(`/tv/settings/items/${item.id}`);
      setItems((cur) => cur.filter((x) => x.id !== item.id));
      toast.success("Slettet");
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  if (loading) {
    return <div className="text-sm text-[#2B3F4D]">Laster…</div>;
  }

  return (
    <div className="space-y-6" data-testid="tv-settings-root">
      {/* --- token + URL ------------------------------------------------- */}
      <Card className="p-5 border-[#BD984D]/30">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[#A57F39]/15 flex items-center justify-center shrink-0">
            <Monitor className="h-5 w-5 text-[#A57F39]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-[#0C1824]">Kontorskjerm-token</h3>
            <p className="text-sm text-[#2B3F4D] mt-1">
              Åpne linken under på skjermen som skal stå på kontoret. Tokenet
              lagres lokalt i nettleseren og utløper aldri – logg deg aldri ut
              av skjermen slik at den forblir aktiv. Ruller tokenet hvis en
              skjerm er kompromittert.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A57F39]">
                  Direkte-URL for skjermen
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    data-testid="tv-url-input"
                    value={tvUrl}
                    readOnly
                    className="font-mono text-sm bg-[#F8F9FA]"
                  />
                  <Button
                    data-testid="tv-copy-url-btn"
                    variant="outline"
                    onClick={() => copy(tvUrl, setUrlCopied)}
                    className="shrink-0"
                  >
                    {urlCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(tvUrl, "_blank")}
                    className="shrink-0"
                    data-testid="tv-open-btn"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A57F39]">
                  TV-token (bruk ved manuell aktivering)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    data-testid="tv-token-value"
                    value={token}
                    readOnly
                    className="font-mono text-sm bg-[#F8F9FA]"
                  />
                  <Button
                    data-testid="tv-copy-token-btn"
                    variant="outline"
                    onClick={() => copy(token, setCopied)}
                    className="shrink-0"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button
                    data-testid="tv-rotate-token-btn"
                    variant="outline"
                    onClick={rotate}
                    className="shrink-0 text-red-600 hover:text-red-700 hover:border-red-300"
                    title="Rullere token (ugyldiggjør alle nåværende skjermer)"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* --- add custom item -------------------------------------------- */}
      <Card className="p-5" data-testid="tv-item-form">
        <h3 className="text-lg font-semibold text-[#0C1824]">
          Legg til nytt oppslag / kampanje
        </h3>
        <p className="text-sm text-[#2B3F4D] mt-1">
          Egendefinerte oppslag vises i «Oppslag &amp; rutiner» på kontorskjermen.
          Daglige gjøremål (avganger, ankomster, vask) hentes automatisk.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label>Tittel *</Label>
            <Input
              data-testid="tv-item-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="F.eks. Sommerkampanje 2026"
              maxLength={120}
            />
          </div>
          <div>
            <Label>Type</Label>
            <Select
              value={form.kind}
              onValueChange={(v) => setForm({ ...form, kind: v })}
            >
              <SelectTrigger data-testid="tv-item-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="announcement">Oppslag</SelectItem>
                <SelectItem value="campaign">Kampanje</SelectItem>
                <SelectItem value="task">Rutine / oppgave</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Tekst</Label>
            <Textarea
              data-testid="tv-item-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Kort beskrivelse som vises under tittelen"
              maxLength={800}
              rows={3}
            />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-[#2B3F4D]">
            <Switch
              data-testid="tv-item-active"
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })}
            />
            Aktiv (vises på skjermen)
          </label>
          <Button
            data-testid="tv-item-add-btn"
            onClick={add}
            disabled={saving || !form.title.trim()}
            className="bg-[#A57F39] hover:bg-[#BD984D]"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Legg til
          </Button>
        </div>
      </Card>

      {/* --- existing items list ---------------------------------------- */}
      <Card className="p-5">
        <h3 className="text-lg font-semibold text-[#0C1824] mb-4">
          Aktive oppslag ({items.length})
        </h3>
        {items.length === 0 ? (
          <p className="text-sm text-[#2B3F4D] italic">
            Ingen egendefinerte oppslag. Daglige gjøremål og ukens rutiner
            (som klesvask tirsdag 12:30) vises automatisk.
          </p>
        ) : (
          <div className="space-y-3" data-testid="tv-items-list">
            {items.map((item) => {
              const meta = KIND_META[item.kind] || KIND_META.announcement;
              const Icon = meta.icon;
              return (
                <div
                  key={item.id}
                  data-testid={`tv-item-${item.id}`}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    item.active
                      ? "bg-white border-[#BD984D]/30"
                      : "bg-[#F8F9FA] border-slate-200 opacity-60"
                  }`}
                >
                  <div className="h-9 w-9 rounded-full bg-[#A57F39]/15 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-[#A57F39]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0C1824]">
                        {item.title}
                      </span>
                      <span className="text-xs uppercase tracking-widest text-[#A57F39]">
                        {meta.label}
                      </span>
                      {!item.active && (
                        <span className="text-xs text-slate-500">Inaktiv</span>
                      )}
                    </div>
                    {item.body && (
                      <p className="text-sm text-[#2B3F4D] mt-1 whitespace-pre-wrap">
                        {item.body}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      data-testid={`tv-item-toggle-${item.id}`}
                      checked={!!item.active}
                      onCheckedChange={() => toggle(item)}
                    />
                    <Button
                      data-testid={`tv-item-delete-${item.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(item)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <RecurringTasksCard />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Recurring weekly tasks (klesvask etc.) – admin editable            */
/* ------------------------------------------------------------------ */
const DAYS = [
  "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag",
];

function RecurringTasksCard() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tv/recurring-tasks");
      setTasks(data.tasks || []);
    } catch {
      toast.error("Kunne ikke hente rutine-oppgaver");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (nextTasks) => {
    setSaving(true);
    try {
      const { data } = await api.put("/tv/recurring-tasks", { tasks: nextTasks });
      setTasks(data.tasks || []);
      toast.success("Lagret");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const update = (idx, patch) => {
    const next = tasks.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    setTasks(next);
  };
  const addTask = () => {
    setTasks([...tasks, {
      title: "", body: "", day_index: 1, time: "12:00",
      show_from_prev_day: true, hide_after_minutes: 5, active: true,
    }]);
  };
  const removeTask = async (idx) => {
    if (!window.confirm("Slette denne rutine-oppgaven?")) return;
    const next = tasks.filter((_, i) => i !== idx);
    save(next);
  };

  return (
    <Card className="p-4 sm:p-6" data-testid="tv-recurring-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#A57F39]" />
            <h3 className="text-base font-semibold text-[#0C1824]">Ukentlige rutiner</h3>
          </div>
          <p className="text-xs text-[#2B3F4D] mt-1 max-w-lg">
            Faste oppslag som dukker opp på TV-en dagen før og forsvinner
            selv etter kort tid – f.eks. «Klesvask – henting». Endre teksten
            eller tidspunktet, eller legg til nye rutiner.
          </p>
        </div>
        <Button onClick={addTask} variant="outline"
                data-testid="tv-recurring-add"
                className="border-[#BD984D] text-[#A57F39]">
          <Plus className="w-4 h-4 mr-1.5" /> Ny rutine
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Laster…</div>
      ) : tasks.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-lg">
          Ingen faste rutiner. Trykk «Ny rutine» for å legge til.
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((t, idx) => (
            <div
              key={t.id || idx}
              className="p-3 sm:p-4 border border-slate-200 rounded-lg space-y-3"
              data-testid={`tv-recurring-row-${idx}`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 sm:gap-3">
                <div>
                  <Label className="text-xs">Tittel</Label>
                  <Input value={t.title}
                         onChange={(e) => update(idx, { title: e.target.value })}
                         data-testid={`tv-recurring-title-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs">Ukedag</Label>
                  <Select
                    value={String(t.day_index)}
                    onValueChange={(v) => update(idx, { day_index: Number(v) })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d, i) => (
                        <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tidspunkt</Label>
                  <Input type="time" value={t.time}
                         onChange={(e) => update(idx, { time: e.target.value })} />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => removeTask(idx)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`tv-recurring-delete-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Beskrivelse</Label>
                <Textarea rows={2} value={t.body}
                          onChange={(e) => update(idx, { body: e.target.value })}
                          placeholder="Kort beskrivelse som vises på TV-skjermen." />
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs text-[#2B3F4D]">
                <label className="flex items-center gap-2">
                  <Switch checked={t.active}
                          onCheckedChange={(v) => update(idx, { active: v })} />
                  <span>{t.active ? "Aktiv" : "Skjult"}</span>
                </label>
                <label className="flex items-center gap-2">
                  <Switch checked={t.show_from_prev_day}
                          onCheckedChange={(v) => update(idx, { show_from_prev_day: v })} />
                  <span>Vis også dagen før</span>
                </label>
                <label className="flex items-center gap-2">
                  <span>Skjul {t.hide_after_minutes} min etter</span>
                  <Input type="number" min={0} max={1440}
                         value={t.hide_after_minutes}
                         onChange={(e) => update(idx, { hide_after_minutes: Number(e.target.value) })}
                         className="h-8 w-20" />
                </label>
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <Button onClick={() => save(tasks)} disabled={saving}
                    data-testid="tv-recurring-save"
                    className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
              Lagre alle rutiner
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
